import React from 'react';
import { Link } from 'react-router-dom';
import { Twitter, Shield, CreditCard, EyeOff, Youtube } from 'lucide-react';

// Discord SVG Icon Component
const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const Footer = () => {
  const footerSections = [
    {
      title: 'For Users',
      links: [
        { name: 'Download for Windows', href: 'https://downloads.twe-assistant.com/latest/twe-voice-assistant-setup.exe', external: true },
        { name: 'Installation Help', href: '/installation' },
        { name: 'Recipe Library', href: '/recipes' },
        { name: 'Pricing', href: '/pricing' }
      ]
    },
    {
      title: 'For Developers',
      links: [
        { name: 'Python SDK Docs', href: '/docs' },
        { name: 'API Reference', href: '/docs' },
        { name: 'Changelog', href: '/changelog' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About the Dev', href: '/about' },
        { name: 'Roadmap', href: '/roadmap' },
        { name: 'Status Page', href: '/status' },
        { name: 'Blog', href: '/blog' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', href: '/privacy' },
        { name: 'Terms of Service', href: '/terms' },
        { name: 'Safety & Security', href: '/safety' }
      ]
    }
  ];

  return (
    <footer className="bg-black border-t border-white/5">
      {/* Trust Bar */}
      <div className="border-b border-white/5 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm">
            <div className="flex items-center space-x-2 text-gray-300">
              <Shield className="w-5 h-5 text-green-500" />
              <span className="font-semibold">Secure & Encrypted</span>
            </div>
            <div className="text-gray-600">•</div>
            <div className="flex items-center space-x-2 text-gray-300">
              <CreditCard className="w-5 h-5 text-green-500" />
              <span className="font-semibold">30-Day Money-Back Guarantee</span>
            </div>
            <div className="text-gray-600">•</div>
            <div className="flex items-center space-x-2 text-gray-300">
              <EyeOff className="w-5 h-5 text-green-500" />
              <span className="font-semibold">No Tracking or Data Selling</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-8">
          {/* Brand Section */}
          <div className="md:col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="/logo.png" 
                alt="Twe Logo" 
                className="h-10 w-10"
              />
              <span className="text-xl font-bold text-white">Twe</span>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Your Voice, Your Browser, Your Rules.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://x.com/MrAshDang" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-orange-500 transition-colors duration-200"
                aria-label="Twitter/X"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href="https://discord.gg/AUezcNccMd" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-orange-500 transition-colors duration-200"
                aria-label="Discord"
              >
                <DiscordIcon className="w-5 h-5" />
              </a>
              <a 
                href="https://www.youtube.com/@twena-inc/videos" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-orange-500 transition-colors duration-200"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Footer Links */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-white font-semibold mb-4">{section.title}</h3>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.name}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm"
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-sm"
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2025 Twe Voice Assistant V3.0. Built by Tu Dang for Power Users Everywhere.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-orange-500 transition-colors duration-200">
                Privacy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-orange-500 transition-colors duration-200">
                Terms
              </Link>
              <Link to="/safety" className="text-gray-400 hover:text-orange-500 transition-colors duration-200">
                Security
              </Link>
              <a 
                href="mailto:contact@twe-assistant.com" 
                className="text-gray-400 hover:text-orange-500 transition-colors duration-200"
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
